import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ListSortDirection, TriConfiguration } from '@lexi-clients/lexi';

export interface TriProperty {
  propertyName: string;
  propertyDisplay: string;
  dataType? : string;
}

@Component({
  selector: 'app-configuration-tri',
  templateUrl: './configuration-tri.component.html',
  styleUrls: ['./configuration-tri.component.scss'],
  standalone: false
})
export class ConfigurationTriComponent implements OnInit {
  @Input() properties: TriProperty[] = [];
  @Input() triConfiguration: TriConfiguration[] = [];
  @Input() disabled : boolean = false;
  @Input() title : string = "Configuration du tri";
  @Output() triConfigurationChange = new EventEmitter<TriConfiguration[]>();

  popupVisible = false;
  
  // Configuration de travail (copie locale)
  private configurationSauvegarde: TriConfiguration[] = [];

  directionsStandard = [
    { value: ListSortDirection.ascending,  libelle: 'Croissant (↑)' },
    { value: ListSortDirection.descending, libelle: 'Décroissant (↓)' }
  ];

  directionsNumeriques = [
    { value: ListSortDirection.ascending,  dataType : 'number',libelle: 'Plus petit (↑)' },
    { value: ListSortDirection.descending, dataType : 'number',libelle: 'Plus grand (↓)' }
  ];

  directionsDates = [
    { value: ListSortDirection.ascending,  dataType  : 'date' , libelle: 'Plus ancien (↑)' },
    { value: ListSortDirection.descending, dataType : 'date' , libelle: 'Plus récent (↓)' }
  ];

  directionsBoolean = [
    { value: ListSortDirection.ascending,  dataType : 'boolean',libelle: 'Vrai (↑)' },
    { value: ListSortDirection.descending, dataType : 'boolean',libelle: 'Faux (↓)' }
  ];

  getPropertyDataType(propertyName: string) : string | undefined {
    const property = this.properties.find(p => p.propertyName === propertyName);
    return property ? property.dataType : undefined;
  }

  get apercuTri(): string {
    if (!this.triConfiguration || this.triConfiguration.length === 0) {
      return 'Aucun tri configuré';
    }

    return this.triConfiguration
      .sort((a, b) => a.ordre - b.ordre)
      .map((config, index) => {
        const property = this.properties.find(p => p.propertyName === config.propertyName);
        const displayName = property ? property.propertyDisplay : config.propertyName;
        const direction = config.direction === ListSortDirection.ascending ? '↑' : '↓';
        return `(${displayName} ${direction})`;
      })
      .join(' > ');
  }

  get proprietesDisponibles() {
    return this.properties.filter(prop => 
      !this.triConfiguration.some(config => config.propertyName === prop.propertyName)
    );
  }

  constructor() { }

  ngOnInit() {}

  displayPopup() {
    // Sauvegarder la configuration actuelle pour pouvoir annuler
    this.configurationSauvegarde = JSON.parse(JSON.stringify(this.triConfiguration));
    this.popupVisible = true;
  }

  // Vérifie si un critère est déjà ajouté
  isCritereDejaAjoute(propertyName: string): boolean {
    return this.triConfiguration.some(config => config.propertyName === propertyName);
  }

  // Ajoute un nouveau critère de tri
  ajouterCritere = (property: TriProperty) => {
    if (!this.isCritereDejaAjoute(property.propertyName)) {
      const nouveauCritere: TriConfiguration = {
        propertyName: property.propertyName,
        direction: ListSortDirection.ascending,
        ordre: this.triConfiguration.length + 1
      };
      this.triConfiguration.push(nouveauCritere);
      this.mettreAJourOrdres();
    }
  };

  // Supprime un critère de tri
  supprimerCritere = (index: number) => {
    this.triConfiguration.splice(index, 1);
    this.mettreAJourOrdres();
  };

  // Gère la réorganisation des critères (drag & drop)
  onReorderCriteres = (event: any) => {
    const { fromIndex, toIndex } = event;
    const item = this.triConfiguration.splice(fromIndex, 1)[0];
    this.triConfiguration.splice(toIndex, 0, item);
    this.mettreAJourOrdres();
  };

  // Met à jour les ordres après réorganisation
  private mettreAJourOrdres() {
    this.triConfiguration.forEach((config, index) => {
      config.ordre = index + 1;
    });
  }

  getPropertyDisplay(propertyName: string): string {
    const property = this.properties.find(p => p.propertyName === propertyName);
    return property ? property.propertyDisplay : propertyName;
  }

  getApercuDetaille(): string {
    if (!this.triConfiguration || this.triConfiguration.length === 0) {
      return 'Aucun tri configuré.\n\nAjoutez des critères depuis la section "Critères disponibles" ci-dessus.';
    }

    const details = this.triConfiguration
      .sort((a, b) => a.ordre - b.ordre)
      .map((config, index) => {
        const property = this.properties.find(p => p.propertyName === config.propertyName);
        const displayName = property ? property.propertyDisplay : config.propertyName;
        const direction = config.direction === ListSortDirection.ascending ? 'croissant' : 'décroissant';
        return `${index + 1}. ${displayName} (${direction})`;
      })
      .join('\n');

    return `Configuration du tri :\n\n${details}\n\nLes éléments seront triés dans cet ordre de priorité.`;
  }

  // Valide et sauvegarde la configuration
  validerConfiguration = () => {
    this.triConfigurationChange.emit([...this.triConfiguration]);
    this.popupVisible = false;
  };

  // Réinitialise la configuration
  reinitialiserConfiguration = () => {
    this.triConfiguration.length = 0; // Vide le tableau en gardant la référence
    this.mettreAJourOrdres();
  };

  // Annule les modifications
  annulerConfiguration = () => {
    // Restaurer la configuration sauvegardée
    this.triConfiguration.length = 0;
    this.triConfiguration.push(...this.configurationSauvegarde);
    this.popupVisible = false;
  };
}
